import { get, post, put, del } from '../lib/apiClient';

/**
 * Event.STATUSES mirrored from src/shared/models/Event.js — the writable
 * `status` values a staff create/edit form and status filter may use.
 */
export const EVENT_STATUSES = ['draft', 'published', 'cancelled', 'completed'];

/** GET /events — upcoming/all member events. Staff may pass `status`. */
export const listEvents = query => get('/events', { query });

/** GET /events/{eventId} */
export const getEvent = eventId => get(`/events/${encodeURIComponent(eventId)}`);

/**
 * POST /events — STAFF (employee/sales/admin). Body allowlist {name,
 * description, venue, startsAt, endsAt, capacity, status, accreditedOnly,
 * metadata}; unknown keys → 400.
 */
export const createEvent = payload => post('/events', payload);

/** PUT /events/{eventId} — STAFF: same allowlist, partial update. */
export const updateEvent = (eventId, payload) =>
  put(`/events/${encodeURIComponent(eventId)}`, payload);

/** POST /events/{eventId}/register — register the caller */
export const registerForEvent = eventId => post(`/events/${encodeURIComponent(eventId)}/register`);

/** DELETE /events/{eventId}/register — cancel the caller's registration */
export const cancelRegistration = eventId => del(`/events/${encodeURIComponent(eventId)}/register`);

/**
 * GET /events/registrations/mine — the caller's own registrations across all
 * events (FEA-16). This is the only source of registration state; the UI
 * must never invent it (docs/FRONTEND-PLAN.md §2.5).
 */
export const listMyRegistrations = query => get('/events/registrations/mine', { query });

/**
 * GET /events/{eventId}/registrations/{registrationId}/badge — JSON envelope
 * `{badge: {registrationId, html}}`. Accessible to staff and to the
 * registration's own attendee.
 */
export const getBadge = (eventId, registrationId) =>
  get(
    `/events/${encodeURIComponent(eventId)}/registrations/${encodeURIComponent(registrationId)}/badge`
  );

/** GET /events/{eventId}/registrations — STAFF: paginated attendee roster. */
export const listRegistrations = (eventId, query) =>
  get(`/events/${encodeURIComponent(eventId)}/registrations`, { query });

/**
 * POST /events/{eventId}/check-in — STAFF (door staff). Body must carry
 * EXACTLY ONE of {registrationId} or {checkInCode}.
 */
export const checkIn = (eventId, body) =>
  post(`/events/${encodeURIComponent(eventId)}/check-in`, body);

/**
 * POST /events/{eventId}/attendance - STAFF (docs/Decisions.md D55). Checks
 * in the registered members whose emails are listed. Resolves to
 * `{ confirmed, alreadyCheckedIn, notRegistered }`, each a list of emails.
 */
export const recordAttendance = (eventId, emails) =>
  post(`/events/${encodeURIComponent(eventId)}/attendance`, { emails });

/**
 * POST /events/{eventId}/badges/print-batch — STAFF. `registrationIds`
 * omitted (or pass no body) renders every non-cancelled registration.
 */
export const printBatch = (eventId, registrationIds) =>
  post(
    `/events/${encodeURIComponent(eventId)}/badges/print-batch`,
    registrationIds ? { registrationIds } : {}
  );
